import { getMyTrees } from "api/trees";
import type { PaginatedList, TreeSummaryDto } from "api/types";
import React, { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import { byEntityTitle } from "./entityFormConfigs";
import type { TreeLinkDraft } from "./entityForms.types";
import LineIcon from "./LineIcon";
import RelationChipItem from "./RelationChipItem";

import styles from "./formDialog.module.css";

type TreeLinkPickerProps = {
	/** Tree the entity belongs to; excluded from the selectable targets. */
	currentTreeId: string;
	links: TreeLinkDraft[];
	onAdd: (treeId: string, title: string) => void;
	onRemove: (draftId: string) => void;
};

/** "Linked Trees" form section: connect the entity to one of the user's other trees. */
const TreeLinkPicker = ({ currentTreeId, links, onAdd, onRemove }: TreeLinkPickerProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();

	const [myTrees, setMyTrees] = useState<TreeSummaryDto[]>([]);
	const [selectedTreeId, setSelectedTreeId] = useState("");

	useEffect(() => {
		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getMyTrees({ pageIndex: 1, pageSize: 100 }, token) : null))
			.then((result: PaginatedList<TreeSummaryDto> | null) => {
				if (active && result) {
					setMyTrees(result.items);
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [getAccessToken]);

	const availableTrees = useMemo<TreeSummaryDto[]>(
		() =>
			myTrees
				.filter(
					(tree: TreeSummaryDto) =>
						tree.id !== currentTreeId && !links.some((link: TreeLinkDraft) => link.treeId === tree.id)
				)
				.sort(byEntityTitle),
		[currentTreeId, links, myTrees]
	);

	const onTargetChange = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		setSelectedTreeId(event.target.value);
	}, []);

	const onAddClick = useCallback((): void => {
		const tree = availableTrees.find((item: TreeSummaryDto) => item.id === selectedTreeId);
		if (!tree) {
			return;
		}

		onAdd(tree.id, tree.title);
		setSelectedTreeId("");
	}, [availableTrees, onAdd, selectedTreeId]);

	const hasTargets = availableTrees.length > 0;

	return (
		<section className={styles.personFormSection}>
			<h2>{t("editor.treeLinks.title")}</h2>
			<div className={styles.relationAddRow}>
				<select
					aria-label={t("editor.treeLinks.target")}
					className={styles.relationSelect}
					disabled={!hasTargets}
					onChange={onTargetChange}
					value={selectedTreeId}>
					<option value="">{t("editor.treeLinks.targetPlaceholder")}</option>
					{availableTrees.map((tree: TreeSummaryDto) => (
						<option key={tree.id} value={tree.id}>
							{tree.title}
						</option>
					))}
				</select>
				<button
					className={styles.relationAddButton}
					disabled={!selectedTreeId}
					onClick={onAddClick}
					type="button">
					<LineIcon name="plus" />
					<span>{t("editor.treeLinks.add")}</span>
				</button>
			</div>

			{!hasTargets && links.length === 0 && <p className={styles.relationHint}>{t("editor.treeLinks.hint")}</p>}

			{links.length > 0 && (
				<div className={styles.relationChips}>
					{links.map((link: TreeLinkDraft) => (
						<RelationChipItem
							key={link.id}
							onRemove={onRemove}
							relation={{
								id: link.id,
								relationType: "LinkedTree",
								isEx: false,
								targetId: link.treeId,
								targetTitle: link.title
							}}
							removeLabel={t("editor.treeLinks.remove")}
						/>
					))}
				</div>
			)}
		</section>
	);
};

export default TreeLinkPicker;
